import React, { useState, useCallback } from 'react';
import { Pencil, Settings } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip } from '../../../components/ui/tooltip';
import { getSequenceColumns, generateSequenceData } from '../helpers/sequenceHelpers';
import { commonConstants } from '../constants/commonConstants';
import { SequenceEditModal } from './SequenceEditModal';
import { SequenceSettingsModal } from './SequenceSettingsModal';
import type { SequenceTableRow } from '../types';

interface SequenceViewProps {
  sequenceList: any[];
  selectionType: string;
  currentPage: number;
  rowsPerPage: number;
  totalCount: number;
  handleTableChange: (pagination: any) => void;
  spreadName: string;
  onClose: () => void;
  canEdit?: boolean;
}

export const SequenceView: React.FC<SequenceViewProps> = ({
  sequenceList,
  selectionType,
  currentPage,
  rowsPerPage,
  totalCount,
  handleTableChange,
  spreadName,
  onClose,
  canEdit = true,
}) => {
  const columns = getSequenceColumns(selectionType);
  const data = generateSequenceData(sequenceList, spreadName) as SequenceTableRow[];

  const [editRow, setEditRow] = useState<SequenceTableRow | null>(null);
  const [settingsRow, setSettingsRow] = useState<SequenceTableRow | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  // Add tooltip to last column
  if (columns.length > 0) {
    const lastColumn = columns[columns.length - 1];
    if (lastColumn.dataIndex === 'tdc') {
      lastColumn.title = (
        <span>
          {lastColumn.title}
          <Tooltip content="Distance (in mm) measured around the pipe from the seam center.
Positive values = clockwise direction
Negative values = anticlockwise direction (viewed from “PipeStart” end).">
            <sup className="cursor-help ml-1">ⓘ</sup>
          </Tooltip>
        </span>
      );
    }
  }

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + rowsPerPage);

  const handlePageSizeChange = (value: string) => {
    handleTableChange({
      current: 1,
      pageSize: Number(value),
    });
  };

  const handlePageChange = (page: number) => {
    handleTableChange({
      current: page,
      pageSize: rowsPerPage,
    });
  };

  const handleEditSave = useCallback(
    async (_row: SequenceTableRow, _values: Partial<SequenceTableRow>) => {
      setEditLoading(true);
      try {
        await Promise.resolve();
        setEditRow(null);
      } finally {
        setEditLoading(false);
      }
    },
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-heritage-grey">Page Size:</span>
          <Select value={rowsPerPage.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {commonConstants.PipeDatapageSizeOptions.map((size) => (
                <SelectItem key={size} value={size}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="text-[12px] text-heritage-grey">
          Showing {startIndex + 1}-{Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount}
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col: any, idx: number) => (
                <TableHead key={idx}>{col.title}</TableHead>
              ))}
              <TableHead className="w-[120px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((row: SequenceTableRow, idx: number) => (
              <TableRow key={row.key ?? idx}>
                {columns.map((col: any, colIdx: number) => (
                  <TableCell key={colIdx}>{row[col.dataIndex as keyof SequenceTableRow]}</TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {/* <Tooltip content="Edit sequence"> */}
                      <Button
                        variant="default"
                        size="icon"
                        onClick={() => setEditRow(row)}
                        disabled={!canEdit}
                        aria-label="Edit sequence"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    {/* </Tooltip> */}
                    <Tooltip content="Sequence settings">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setSettingsRow(row)}
                        disabled={!canEdit}
                        aria-label="Sequence settings"
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-[12px] text-heritage-grey">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={onClose}>Close</Button>
      </div>

      <SequenceEditModal
        open={editRow !== null}
        onOpenChange={(open) => !open && setEditRow(null)}
        row={editRow}
        onSave={handleEditSave}
        loading={editLoading}
      />
      <SequenceSettingsModal
        open={settingsRow !== null}
        onOpenChange={(open) => !open && setSettingsRow(null)}
        row={settingsRow}
        loading={false}
      />
    </div>
  );
};
